import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import { createEnvelope, duplicateEnvelope } from "../../../actions/signatures";
import { send as sendAlert } from "../../../actions/alert";
import { send as pageInfo } from "../../../actions/page";
import CsLineIcons from "../../../cs-line-icons";
import { SIGNATURE_METADATA_FIELDS_ENABLED } from "../features";

const CREATION_SOURCE_OPTIONS = [
  { value: "upload", label: "Upload New Document" },
  { value: "document_selection", label: "Select Document" },
  { value: "template", label: "Select Template" },
  { value: "matter", label: "Create From Matter" },
  { value: "client_portal", label: "Create From Client Portal" },
  { value: "api", label: "Create From API" },
  { value: "document_generation", label: "Create From Document Generation" },
  { value: "duplicate", label: "Duplicate Prior Envelope" },
  { value: "voided_duplicate", label: "Duplicate Voided Envelope" },
  { value: "self_sign", label: "Create Self-Sign Envelope" },
];

const DUPLICATE_SOURCES = new Set(["duplicate", "voided_duplicate"]);
const OWNER_TYPE_OPTIONS = [
  { value: "person", label: "Person" },
  { value: "matter", label: "Matter" },
  { value: "lead", label: "Lead" },
  { value: "firm", label: "Firm" },
  { value: "signature", label: "Signature" },
];

const initialForm = (user = {}) => ({
  creationSource: "upload",
  title: "",
  subject: "",
  messageBody: "Please review and sign this document.",
  internalNotes: "",
  externalNotes: "",
  sourceFileUuid: "",
  templateUuid: "",
  roleMappingsJson: "",
  templateSignersJson: "",
  templateDataJson: "",
  matterUuid: "",
  leadUuid: "",
  personUuid: "",
  ownerPersonUuid: "",
  firmUuid: "",
  senderUuid: user.uuid || "",
  originalFileOwnerType: "",
  originalFileOwnerUuid: "",
  documentOwnerType: "",
  documentOwnerUuid: "",
  clientPortalUuid: "",
  generatedDocumentUuid: "",
  documentGenerationWorkflowUuid: "",
  sourceEnvelopeUuid: "",
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  sourceSystem: "matters.firm",
  apiClientId: "",
  tags: "",
  category: "",
  practiceArea: "",
  documentType: "",
  signerName: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.displayName || user.name || "",
  signerEmail: user.email || user.userName || user.username || "",
});

const selectOption = (options, value) => options.find((option) => option.value === value) || null;
const valueOrNull = (value) => String(value || "").trim() || null;
const formatSignatureError = (error) => {
  if (!error) return "Unable to create the signature request.";
  if (typeof error === "string") return error;
  const message = error.message || "Unable to create the signature request.";
  const details = error.details || {};
  const unknown = Array.isArray(details.unknown) && details.unknown.length
    ? ` Unknown fields: ${details.unknown.join(", ")}.`
    : "";
  const available = Array.isArray(details.available) && details.available.length
    ? ` Available fields: ${details.available.join(", ")}.`
    : "";
  return `${message}${unknown}${available}`;
};
const parseJsonInput = (value, label) => {
  const text = String(value || "").trim();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`${label} must contain valid JSON.`);
  }
};

function SignatureEnvelopeCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth?.user || {});
  const [form, setForm] = useState(() => initialForm(authUser));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const creationSourceOption = useMemo(() => selectOption(CREATION_SOURCE_OPTIONS, form.creationSource), [form.creationSource]);
  const duplicateMode = DUPLICATE_SOURCES.has(form.creationSource);
  const selfSignMode = form.creationSource === "self_sign";

  useEffect(() => {
    dispatch(pageInfo({
      title: "Create Signature Request",
      breadcrumb: "Create",
      description: "Prepare and send a signature envelope",
    }));
  }, [dispatch]);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateCreationSource = (option) => {
    setError("");
    setForm((current) => ({ ...current, creationSource: option?.value || "upload" }));
  };

  const validate = () => {
    if (duplicateMode && !valueOrNull(form.sourceEnvelopeUuid)) return "Envelope UUID is required.";
    if (form.creationSource === "upload" && !file) return "Document upload is required.";
    if (form.creationSource === "document_selection" && !valueOrNull(form.sourceFileUuid)) return "Source file UUID is required.";
    if (form.creationSource === "template" && !valueOrNull(form.templateUuid)) return "Template UUID is required.";
    if (form.creationSource === "matter" && !valueOrNull(form.matterUuid)) return "Matter UUID is required.";
    if (form.creationSource === "client_portal" && !valueOrNull(form.clientPortalUuid)) return "Client portal UUID is required.";
    if (form.creationSource === "document_generation" && !valueOrNull(form.generatedDocumentUuid) && !valueOrNull(form.documentGenerationWorkflowUuid)) {
      return "Generated document or workflow UUID is required.";
    }
    if (selfSignMode && (!valueOrNull(form.signerName) || !valueOrNull(form.signerEmail))) {
      return "Self-sign name and email are required.";
    }
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    let roleMappings;
    let templateSigners;
    let templateData;
    try {
      roleMappings = parseJsonInput(form.roleMappingsJson, "Role mappings");
      templateSigners = parseJsonInput(form.templateSignersJson, "Template signers");
      templateData = parseJsonInput(form.templateDataJson, "Template field values");
      if (roleMappings !== undefined && !Array.isArray(roleMappings)) throw new Error("Role mappings must be a JSON array.");
      if (templateSigners !== undefined && !Array.isArray(templateSigners)) throw new Error("Template signers must be a JSON array.");
      if (templateData !== undefined && (!templateData || typeof templateData !== "object" || Array.isArray(templateData))) {
        throw new Error("Template field values must be a JSON object keyed by API field name.");
      }
    } catch (parseError) {
      setSaving(false);
      setError(parseError.message);
      return;
    }

    const envelopePayload = {
      creationSource: form.creationSource,
      sourceApplication: "matters.firm.signatures.create",
      file,
      originalFileName: file?.name || null,
      title: valueOrNull(form.title) || file?.name || null,
      displayName: valueOrNull(form.title) || file?.name || null,
      subject: valueOrNull(form.subject),
      messageBody: valueOrNull(form.messageBody),
      sourceFileUuid: valueOrNull(form.sourceFileUuid),
      templateUuid: valueOrNull(form.templateUuid),
      roleMappings,
      signers: templateSigners,
      data: templateData,
      matterUuid: valueOrNull(form.matterUuid),
      leadUuid: valueOrNull(form.leadUuid),
      personUuid: valueOrNull(form.personUuid) || valueOrNull(form.ownerPersonUuid),
      ownerPersonUuid: valueOrNull(form.ownerPersonUuid) || valueOrNull(form.personUuid),
      firmUuid: valueOrNull(form.firmUuid),
      senderUuid: valueOrNull(form.senderUuid),
      originalFileOwnerType: valueOrNull(form.originalFileOwnerType),
      originalFileOwnerUuid: valueOrNull(form.originalFileOwnerUuid),
      documentOwnerType: valueOrNull(form.documentOwnerType),
      documentOwnerUuid: valueOrNull(form.documentOwnerUuid),
      clientPortalUuid: valueOrNull(form.clientPortalUuid),
      generatedDocumentUuid: valueOrNull(form.generatedDocumentUuid),
      documentGenerationWorkflowUuid: valueOrNull(form.documentGenerationWorkflowUuid),
      timeZone: valueOrNull(form.timeZone),
      sourceSystem: valueOrNull(form.sourceSystem),
      apiClientId: valueOrNull(form.apiClientId),
      tags: valueOrNull(form.tags),
      selfSign: selfSignMode,
      signerName: valueOrNull(form.signerName),
      signerEmail: valueOrNull(form.signerEmail),
      metadata: {
        source: "matters.firm.signatures.create",
        creationSource: form.creationSource,
      },
    };

    if (SIGNATURE_METADATA_FIELDS_ENABLED) {
      Object.assign(envelopePayload, {
        internalNotes: valueOrNull(form.internalNotes),
        externalNotes: valueOrNull(form.externalNotes),
        category: valueOrNull(form.category),
        practiceArea: valueOrNull(form.practiceArea),
        documentType: valueOrNull(form.documentType),
      });
    }

    const result = duplicateMode
      ? await dispatch(duplicateEnvelope({ uuid: valueOrNull(form.sourceEnvelopeUuid) }))
      : await dispatch(createEnvelope(envelopePayload));

    setSaving(false);

    if (!result?.success) {
      setError(formatSignatureError(result?.error));
      return;
    }

    const envelopeUuid = result.item?.uuid || result.uuid || null;
    dispatch(sendAlert("Signature request created.", "success"));
    if (envelopeUuid) navigate(`/dashboard/signatures/${envelopeUuid}`);
  };

  const renderTextInput = (name, label, { type = "text", md = 6 } = {}) => (
    <Col md={md}>
      <div className="top-label">
        <Form.Control
          type={type}
          name={name}
          value={form[name]}
          onChange={updateForm}
          disabled={saving}
        />
        <Form.Label>{label}</Form.Label>
      </div>
    </Col>
  );

  const renderOwnerTypeSelect = (name, label) => (
    <Col md="3">
      <div className="top-label">
        <Select
          classNamePrefix="react-select"
          options={OWNER_TYPE_OPTIONS}
          value={selectOption(OWNER_TYPE_OPTIONS, form[name])}
          onChange={(option) => setForm((current) => ({ ...current, [name]: option?.value || "" }))}
          isClearable
          isDisabled={saving}
        />
        <Form.Label>{label}</Form.Label>
      </div>
    </Col>
  );

  return (
    <Col xs="12">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="mb-0">Create Signature Request</Card.Title>
            <Badge bg="light" text="dark">{creationSourceOption?.label || "Envelope"}</Badge>
          </div>

          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Form onSubmit={submit}>
            <Row className="g-3">
              <Col lg="6">
                <div className="top-label">
                  <Select
                    classNamePrefix="react-select"
                    options={CREATION_SOURCE_OPTIONS}
                    value={creationSourceOption}
                    onChange={updateCreationSource}
                    isDisabled={saving}
                  />
                  <Form.Label>CREATE FROM</Form.Label>
                </div>
              </Col>

              {duplicateMode ? (
                renderTextInput("sourceEnvelopeUuid", "ENVELOPE UUID", { md: 6 })
              ) : (
                <>
                  {["upload", "self_sign"].includes(form.creationSource) ? (
                    <Col md="6">
                      <div className="top-label">
                        <Form.Control
                          type="file"
                          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(event) => setFile(event.target.files?.[0] || null)}
                          disabled={saving}
                        />
                        <Form.Label>DOCUMENT</Form.Label>
                      </div>
                    </Col>
                  ) : null}

                  {["document_selection", "matter", "client_portal", "document_generation", "self_sign"].includes(form.creationSource)
                    ? renderTextInput("sourceFileUuid", "SOURCE FILE UUID", { md: 6 })
                    : null}

                  {form.creationSource === "template" ? renderTextInput("templateUuid", "TEMPLATE UUID", { md: 6 }) : null}
                  {form.creationSource === "template" ? (
                    <>
                      <Col md="6">
                        <div className="top-label">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="roleMappingsJson"
                            value={form.roleMappingsJson}
                            onChange={updateForm}
                            disabled={saving}
                            placeholder='[{"roleUuid":"...","name":"Jane Doe","email":"jane@example.com"}]'
                          />
                          <Form.Label>ROLE MAPPINGS (JSON)</Form.Label>
                          <Form.Text>Map each required template role to a name and email. Context signers may use the template context through the API action.</Form.Text>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="top-label">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="templateSignersJson"
                            value={form.templateSignersJson}
                            onChange={updateForm}
                            disabled={saving}
                            placeholder='[{"context":"matter-client","signer":{"name":"Jane Doe","email":"jane@example.com"}}]'
                          />
                          <Form.Label>CONTEXT SIGNERS (JSON)</Form.Label>
                          <Form.Text>For digital signing contexts, provide the context and signer object accepted by FeraSign.</Form.Text>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="top-label">
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="templateDataJson"
                            value={form.templateDataJson}
                            onChange={updateForm}
                            disabled={saving}
                            placeholder='{"signer-full-name":"Jane Doe"}'
                          />
                          <Form.Label>API FIELD VALUES (JSON)</Form.Label>
                          <Form.Text>Use the exact API field names defined by the prepared template.</Form.Text>
                        </div>
                      </Col>
                    </>
                  ) : null}
                  {form.creationSource === "client_portal" ? renderTextInput("clientPortalUuid", "CLIENT PORTAL UUID", { md: 6 }) : null}
                  {form.creationSource === "document_generation" ? renderTextInput("generatedDocumentUuid", "GENERATED DOCUMENT UUID", { md: 6 }) : null}
                  {form.creationSource === "document_generation" ? renderTextInput("documentGenerationWorkflowUuid", "WORKFLOW UUID", { md: 6 }) : null}
                  {renderTextInput("matterUuid", "MATTER UUID", { md: 4 })}
                  {renderTextInput("leadUuid", "LEAD UUID", { md: 4 })}
                  {renderTextInput("personUuid", "PERSON UUID", { md: 4 })}
                  {renderTextInput("firmUuid", "FIRM UUID", { md: 4 })}
                  {renderTextInput("senderUuid", "SENDER UUID", { md: 4 })}
                  {renderTextInput("ownerPersonUuid", "OWNER PERSON UUID", { md: 4 })}
                  {renderOwnerTypeSelect("originalFileOwnerType", "ORIGINAL OWNER TYPE")}
                  {renderTextInput("originalFileOwnerUuid", "ORIGINAL OWNER UUID", { md: 3 })}
                  {renderOwnerTypeSelect("documentOwnerType", "DOCUMENT OWNER TYPE")}
                  {renderTextInput("documentOwnerUuid", "DOCUMENT OWNER UUID", { md: 3 })}
                  {selfSignMode ? renderTextInput("signerName", "SIGNER NAME", { md: 6 }) : null}
                  {selfSignMode ? renderTextInput("signerEmail", "SIGNER EMAIL", { type: "email", md: 6 }) : null}
                  {renderTextInput("title", "ENVELOPE TITLE", { md: 6 })}
                  {renderTextInput("subject", "SUBJECT", { md: 6 })}
                  <Col xs="12">
                    <div className="top-label">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="messageBody"
                        value={form.messageBody}
                        onChange={updateForm}
                        disabled={saving}
                      />
                      <Form.Label>MESSAGE</Form.Label>
                    </div>
                  </Col>
                  {SIGNATURE_METADATA_FIELDS_ENABLED ? (
                    <>
                      <Col md="6">
                        <div className="top-label">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="internalNotes"
                            value={form.internalNotes}
                            onChange={updateForm}
                            disabled={saving}
                          />
                          <Form.Label>INTERNAL NOTES</Form.Label>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="top-label">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="externalNotes"
                            value={form.externalNotes}
                            onChange={updateForm}
                            disabled={saving}
                          />
                          <Form.Label>EXTERNAL NOTES</Form.Label>
                        </div>
                      </Col>
                    </>
                  ) : null}
                  {renderTextInput("timeZone", "TIME ZONE", { md: 4 })}
                  {renderTextInput("sourceSystem", "SOURCE SYSTEM", { md: 4 })}
                  {renderTextInput("apiClientId", "API CLIENT ID", { md: 4 })}
                  {SIGNATURE_METADATA_FIELDS_ENABLED ? (
                    <>
                      {renderTextInput("category", "CATEGORY", { md: 4 })}
                      {renderTextInput("practiceArea", "PRACTICE AREA", { md: 4 })}
                      {renderTextInput("documentType", "DOCUMENT TYPE", { md: 4 })}
                    </>
                  ) : null}
                  {renderTextInput("tags", "TAGS", { md: SIGNATURE_METADATA_FIELDS_ENABLED ? 6 : 12 })}
                </>
              )}
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button type="button" variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>
                Back
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <CsLineIcons icon="pen" size="15" className="me-1" />}
                Create
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Col>
  );
}

export default SignatureEnvelopeCreate;
